import { clsx } from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { isPopoverOpen } from '@repo/dom/dom';
import { Flex } from '@repo/ui/Flex';
import { Toast, type ToastPosition } from '@repo/ui/Toast';
import { type ToastItem } from '@repo/ui/Toast/ToastRoot';

import s from './ToastGroup.module.css';

const PORTAL_ID = 'toast-portal';

interface ToastContainerProps {
  toasts: ToastItem[];
  hideToast: (id: string) => void;
}

export const ToastGroup = ({ toasts, hideToast }: ToastContainerProps) => {
  const [portalElement] = useState(() => {
    if (typeof document === 'undefined') return null;

    const existingElement = document.getElementById(PORTAL_ID);
    const element = existingElement ?? document.createElement('div');
    element.id = PORTAL_ID;
    element.className = s.portal;
    // Render in the top layer so toasts clear modal <dialog> overlays (which use
    // showModal + the top layer, above any z-index). Ignored by engines without
    // popover, which fall back to the plain z-index behavior.
    element.setAttribute('popover', 'manual');
    if (!existingElement) document.body.appendChild(element);
    return element;
  });

  // Show the portal as a popover while toasts exist, so a toast fired from within
  // an open modal lands in the top layer above it. Keyed off presence only, so
  // adding/removing a toast while others remain doesn't re-add the already-visible
  // ones (which would restart their entry animation and can re-announce role=alert).
  const hasToasts = toasts.length > 0;
  useEffect(() => {
    if (!portalElement) return;
    if (isPopoverOpen(portalElement) === hasToasts) return;
    if (hasToasts) portalElement.showPopover?.();
    else portalElement.hidePopover?.();
  }, [hasToasts, portalElement]);

  // Group toasts by position
  const toastsByPosition = useMemo(() => {
    const grouped: Record<ToastPosition, ToastItem[]> = {
      topLeft: [],
      topCenter: [],
      topRight: [],
      bottomRight: [],
      bottomCenter: [],
      bottomLeft: [],
    };

    toasts.forEach((toast) => {
      const position = toast.position ?? 'bottomRight';
      grouped[position].push(toast);
    });

    return grouped;
  }, [toasts]);

  const toastElements = useMemo(
    () => (
      <>
        {(
          Object.entries(toastsByPosition) as [ToastPosition, ToastItem[]][]
        ).map(([position, positionToasts]) => {
          if (positionToasts.length === 0) return null;

          return (
            <Flex
              key={position}
              className={clsx(s.group, s[`${position}Position`])}
              display="inline-flex"
              direction="column"
              gap="g-200"
            >
              {positionToasts.map((toast) => (
                <Toast
                  key={toast.id}
                  action={toast.action}
                  variant={toast.variant}
                  duration={toast.duration}
                  autoClose={toast.autoClose}
                  onClose={() => hideToast(toast.id)}
                >
                  {toast.children}
                </Toast>
              ))}
            </Flex>
          );
        })}
      </>
    ),
    [toastsByPosition, hideToast],
  );

  if (!portalElement) return null;
  return createPortal(toastElements, portalElement);
};
